/**
 * Account settings: "Notifications on this device" on/off. On asks the browser (from this click only);
 * off stops pushes to this browser and keeps the dashboard from asking again here. Every other device
 * keeps its own setting.
 */
import { useEffect, useState } from 'react';
import { Switch } from '../ui/switch';
import { isPushOnHere, permissionState, pushSupport, turnOffPushHere, turnOnPush } from '../../lib/notifications/push';
import { PUSH_OFF_HERE_KEY } from './PushPromptBanner';

export default function PushDeviceSwitch() {
  const support = pushSupport();
  const [on, setOn] = useState(false);
  const [checked, setChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    isPushOnHere().then((v) => { setOn(v); setChecked(true); }).catch(() => setChecked(true));
  }, []);

  const blocked = permissionState() === 'denied';
  const disabledWhy =
    support.kind === 'ios_needs_home_screen'
      ? 'On iPhone and iPad, add Arevina to your Home Screen first (Share, then Add to Home Screen), then open it from there.'
      : support.kind === 'unsupported'
        ? 'This browser does not support notifications.'
        : blocked
          ? "Notifications are blocked for Arevina in this browser. Allow them in the browser's site settings, then come back."
          : null;

  const toggle = async (next: boolean) => {
    setBusy(true);
    setNote(null);
    try {
      if (next) {
        const r = await turnOnPush();
        if (r === 'on') {
          setOn(true);
          try { localStorage.removeItem(PUSH_OFF_HERE_KEY); } catch { /* ignore */ }
        } else if (r === 'denied') setNote("Notifications are blocked for Arevina in this browser. Allow them in the browser's site settings.");
        else if (r !== 'dismissed') setNote('Could not turn on notifications on this device. Please try again.');
      } else {
        await turnOffPushHere();
        setOn(false);
        try { localStorage.setItem(PUSH_OFF_HERE_KEY, '1'); } catch { /* ignore */ }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-start justify-between gap-3 py-2" data-testid="push-device-setting" data-on={on ? 'true' : 'false'}>
      <div className="min-w-0">
        <p className="text-sm font-medium text-[var(--text-primary)]">Notifications on this device</p>
        <p className="text-xs text-[var(--text-muted)]">
          Interview requests and answers, confirmed times, the day-before reminder, new applications and new messages, on this browser even when Arevina is closed.
        </p>
        {disabledWhy && <p className="mt-1 text-xs text-[var(--text-secondary)]" data-testid="push-device-why">{disabledWhy}</p>}
        {note && <p className="mt-1 text-xs text-[var(--text-secondary)]" role="status">{note}</p>}
      </div>
      <Switch
        checked={on}
        disabled={busy || !checked || !!disabledWhy}
        onCheckedChange={(v) => void toggle(v)}
        aria-label="Notifications on this device"
        data-testid="push-device-switch"
      />
    </div>
  );
}
